import { InputHTMLAttributes, forwardRef } from 'react'
import clsx from 'clsx'

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  error?: string
  helperText?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'filled' | 'outlined'
  fullWidth?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ 
    label, 
    error, 
    helperText, 
    size = 'md',
    variant = 'default',
    fullWidth = false,
    leftIcon,
    rightIcon,
    className,
    id,
    ...props 
  }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`
    
    const baseClasses = 'transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-accent-500 disabled:opacity-50 disabled:cursor-not-allowed'
    
    const sizeClasses = {
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-5 py-4 text-lg'
    }
    
    const variantClasses = {
      default: 'bg-white border border-primary-300 rounded-lg focus:border-accent-500',
      filled: 'bg-primary-50 border border-transparent rounded-lg focus:bg-white focus:border-accent-500',
      outlined: 'bg-transparent border-2 border-primary-400 rounded-lg focus:border-accent-500'
    }
    
    const errorClasses = error 
      ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
      : ''
    
    const inputClasses = clsx(
      baseClasses,
      sizeClasses[size],
      variantClasses[variant],
      errorClasses,
      leftIcon && 'pl-12',
      rightIcon && 'pr-12',
      fullWidth && 'w-full',
      className
    )

    return (
      <div className={clsx('relative', fullWidth && 'w-full')}>
        {label && (
          <label 
            htmlFor={inputId}
            className="form-label"
          >
            {label}
          </label>
        )}
        
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <div className="h-5 w-5 text-primary-500">
                {leftIcon}
              </div>
            </div>
          )}
          
          <input
            ref={ref}
            id={inputId}
            className={inputClasses}
            {...props}
          />
          
          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <div className="h-5 w-5 text-primary-500">
                {rightIcon}
              </div>
            </div>
          )}
        </div>
        
        {error && (
          <p className="form-error">
            {error}
          </p>
        )}
        
        {helperText && !error && (
          <p className="text-primary-600 text-caption mt-1">
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export default Input
